import React, { useRef, useEffect, useState } from 'react';
import { useDrag } from '@use-gesture/react';
import { motion } from 'framer-motion';

export default function TableItem({
  table,
  allTables,
  onStopDrag,
  onDoubleClick,
  onClickTable,
  draggable = true,
  isInteractive = false,
}) {
  const { shape, tableNumber, rotation = 0, locked, seats = 4, _id, posX, posY, status = 'available' } = table;
  const [position, setPosition] = useState({ x: posX, y: posY });
  const boxRef = useRef(null);
  const gridSize = 20;

  useEffect(() => {
    setPosition({ x: posX, y: posY });
  }, [posX, posY]);

  const getDimensions = () => {
    if (shape === 'rectangle') return { width: 240, height: 100 };
    return { width: 120, height: 120 };
  };
  const { width, height } = getDimensions();

  const isOverlapping = (x, y) => {
    return allTables.some((t) => {
      if (t._id === _id) return false;
      const tWidth = t.shape === 'rectangle' ? 240 : 100;
      const tHeight = t.shape === 'rectangle' ? 100 : 100;
      return (
        x < t.posX + tWidth &&
        x + width > t.posX &&
        y < t.posY + tHeight &&
        y + height > t.posY
      );
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
        return '#d6f6d5';
      case 'occupied':
        return '#f87171';
      case 'reserved':
        return '#facc15';
      case 'merged':
        return '#a78bfa';
      default:
        return '#f5f5f5';
    }
  };

  const bind = useDrag(({ offset: [x, y], last }) => {
    const snappedX = Math.round(x / gridSize) * gridSize;
    const snappedY = Math.round(y / gridSize) * gridSize;

    if (!isOverlapping(snappedX, snappedY)) {
      setPosition({ x: snappedX, y: snappedY });

      if (last && onStopDrag) {
        onStopDrag(table, null, { x: snappedX, y: snappedY });
      }
    }
  }, {
    from: () => [position.x, position.y],
    bounds: () => {
      const floor = document.querySelector('.floor-area');
      const rect = floor?.getBoundingClientRect();
      const maxX = rect ? rect.width - width : 1000;
      const maxY = rect ? rect.height - height : 600;
      return { left: 0, top: 0, right: maxX, bottom: maxY };
    },
    rubberband: false,
    enabled: draggable,
  });

  return (
    <motion.div
      ref={boxRef}
      {...(draggable ? bind() : {})}
      drag={false}
      animate={{ x: position.x, y: position.y }}
      initial={false}
      whileTap={{ scale: draggable ? 0.95 : 1 }}
      whileHover={isInteractive ? { boxShadow: '0 0 0 3px rgba(59,130,246,0.5)' } : {}}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      onDoubleClick={() => draggable && onDoubleClick?.(table)}
      onClick={() => isInteractive && onClickTable?.(table)}
      style={{
        width,
        height,
        borderRadius: shape === 'circle' ? '50%' : '0',
        backgroundColor: getStatusColor(status),
        border: '2px solid #888',
        position: 'absolute',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        fontWeight: 600,
        fontSize: '16px',
        cursor: locked ? 'not-allowed' : draggable ? 'grab' : isInteractive ? 'pointer' : 'default',
        userSelect: 'none',
        transformOrigin: 'top left',
        rotate: `${rotation}deg`,
        padding: '8px',
        textAlign: 'center',
      }}
    >
      <div>{tableNumber}</div>
      <div>Max {seats} seats</div>
    </motion.div>
  );
}
